import React, { useState, useEffect } from 'react';
import { Ticket, X, Send, AlertCircle, ShieldCheck, Mail, Smartphone, KeyRound, CheckCircle2 } from 'lucide-react';

export function TicketForm({ onClose, onTicketCreated, initialSubject = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: initialSubject || '',
    message: '',
    priority: 'normal',
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');

  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [otpStatusMsg, setOtpStatusMsg] = useState('');

  useEffect(() => {
    if (initialSubject) {
      setFormData((prev) => ({ ...prev, subject: initialSubject }));
    }
  }, [initialSubject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If user changes the email address, reset OTP verification state immediately
    if (name === 'email') {
      setOtpSent(false);
      setOtpVerified(false);
      setVerificationToken('');
      setOtpCode('');
      setOtpStatusMsg('');
    }
  };

  const handleSendOtp = async () => {
    setErrorMsg('');
    setOtpStatusMsg('');

    const rawEmail = formData.email.trim();
    if (!rawEmail || !rawEmail.includes('@')) {
      setErrorMsg('Please enter a valid email address first.');
      return;
    }

    setIsSendingOtp(true);
    try {
      const res = await fetch('/api/tickets/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: rawEmail }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send verification code to your email.');
      }

      setOtpSent(true);
      setOtpVerified(false);
      setVerificationToken('');
      setOtpCode('');
      setOtpStatusMsg(data.message || 'Verification code sent to your email address via Gmail SMTP.');
    } catch (err) {
      setErrorMsg(err.message || 'Unable to send verification code. Please check your email.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setErrorMsg('');
    setOtpStatusMsg('');

    if (!otpCode.trim() || otpCode.trim().length !== 6) {
      setErrorMsg('Please enter the 6-digit verification code sent to your email.');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const res = await fetch('/api/tickets/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim(),
          otp: otpCode.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.verified) {
        setOtpVerified(false);
        setVerificationToken('');
        throw new Error(data.error || 'Invalid verification code. Ticket creation is prevented until verification succeeds.');
      }

      setOtpVerified(true);
      setVerificationToken(data.verificationToken || '');
      setOtpStatusMsg('Email verified successfully via Gmail SMTP! You can now create your ticket.');
    } catch (err) {
      setOtpVerified(false);
      setVerificationToken('');
      setErrorMsg(err.message || 'Verification failed. Please check the code in your email.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setErrorMsg('Please fill in your Name, Email, and Issue description.');
      return;
    }

    // Strictly prevent ticket creation or storage if OTP verification is not completed
    if (!otpVerified || !verificationToken) {
      setErrorMsg('Please verify your email address via OTP before creating a support ticket.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          otp: otpCode.trim(),
          verificationToken,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit support ticket.');
      }

      onTicketCreated(data.ticket);
      onClose();
    } catch (err) {
      console.error('Ticket submission error:', err);
      setErrorMsg(err.message || 'Failed to submit ticket. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ticket-form-overlay">
      <div className="ticket-form-card">
        <div className="ticket-form-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Ticket size={18} color="#6366f1" />
            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>Raise a Support Ticket</h4>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="icon-btn-close"
            aria-label="Close form"
          >
            <X size={16} />
          </button>
        </div>

        <p className="ticket-form-desc">
          Enter your details and verify your email address via Gmail SMTP to create your verified ticket.
        </p>

        {errorMsg && (
          <div className="ticket-error-alert" role="alert">
            <AlertCircle size={14} style={{ flexShrink: 0 }} />
            <span>{errorMsg}</span>
          </div>
        )}

        {otpStatusMsg && !errorMsg && (
          <div className="ticket-otp-status-alert">
            <CheckCircle2 size={14} style={{ flexShrink: 0 }} />
            <span>{otpStatusMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="ticket-form-fields">
          <div className="ticket-input-row">
            <div className="ticket-input-group">
              <label>Your Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Jane Doe"
                required
              />
            </div>
            <div className="ticket-input-group">
              <label>Contact Phone (Optional)</label>
              <div className="ticket-mobile-input-wrap">
                <Smartphone size={14} className="ticket-input-icon" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g. 9876543210"
                />
              </div>
            </div>
          </div>

          {/* Email Address + Send OTP Row */}
          <div className="ticket-input-group">
            <label>Your Email Address (Gmail SMTP OTP Verification Required) *</label>
            <div className="ticket-mobile-otp-row">
              <div className="ticket-mobile-input-wrap">
                <Mail size={14} className="ticket-input-icon" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="yourname@gmail.com"
                  required
                  disabled={otpVerified}
                />
              </div>
              {otpVerified ? (
                <span className="otp-verified-badge" title="Email verified via Gmail SMTP">
                  <ShieldCheck size={14} />
                  <span>Email Verified</span>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isSendingOtp || !formData.email.trim()}
                  className="btn-send-otp"
                >
                  {isSendingOtp ? 'Sending...' : otpSent ? 'Resend Code' : 'Send OTP'}
                </button>
              )}
            </div>
          </div>

          {/* 6-Digit OTP Verification Box */}
          {otpSent && !otpVerified && (
            <div className="ticket-otp-verify-box">
              <p style={{ margin: '0 0 6px 0', fontSize: '11.5px', color: '#475569' }}>
                Check your email inbox (and spam/promotions folder) for the 6-digit verification code.
              </p>
              <div className="ticket-mobile-otp-row">
                <div className="ticket-mobile-input-wrap">
                  <KeyRound size={14} className="ticket-input-icon" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    aria-label="Enter 6-digit code"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={isVerifyingOtp || otpCode.trim().length !== 6}
                  className="btn-verify-otp"
                >
                  {isVerifyingOtp ? 'Verifying...' : 'Verify Code'}
                </button>
              </div>
            </div>
          )}

          <div className="ticket-input-row">
            <div className="ticket-input-group" style={{ flex: 2 }}>
              <label>Subject</label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="e.g. Demo Class / Software Development Quote"
              />
            </div>
            <div className="ticket-input-group" style={{ flex: 1 }}>
              <label>Priority</label>
              <select name="priority" value={formData.priority} onChange={handleChange}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="ticket-input-group">
            <label>Detailed Message / Inquiry Description *</label>
            <textarea
              name="message"
              rows={3}
              value={formData.message}
              onChange={handleChange}
              placeholder="Describe your class inquiry or software project requirements..."
              required
            />
          </div>

          <div className="ticket-form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit-ticket"
              disabled={isSubmitting || !otpVerified}
              title={!otpVerified ? 'Verify your email address via OTP first' : 'Create verified support ticket'}
            >
              <Send size={14} />
              <span>{isSubmitting ? 'Creating...' : 'Create Verified Ticket'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
